import React, { useState } from 'react';
import { X, Car, Battery, Zap, Check, AlertTriangle, ChevronRight, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

// Noco GBX45 compatibility: up to 6.5L gasoline, 4L diesel
const CAR_DATABASE = {
  // Format: brand -> model -> { type: 'gas'|'diesel'|'hybrid', engineSize: liters }
  'Audi': {
    'A1': { type: 'gas', engineSize: 1.5 },
    'A3': { type: 'gas', engineSize: 2.0 },
    'A4': { type: 'gas', engineSize: 2.0 },
    'A5': { type: 'gas', engineSize: 2.0 },
    'A6': { type: 'gas', engineSize: 3.0 },
    'A7': { type: 'gas', engineSize: 3.0 },
    'A8': { type: 'gas', engineSize: 4.0 },
    'Q3': { type: 'gas', engineSize: 2.0 },
    'Q5': { type: 'gas', engineSize: 2.0 },
    'Q7': { type: 'diesel', engineSize: 3.0 },
    'Q8': { type: 'gas', engineSize: 4.0 },
    'TT': { type: 'gas', engineSize: 2.0 },
    'e-tron': { type: 'electric', engineSize: 0 },
  },
  'BMW': {
    'Serie 1': { type: 'gas', engineSize: 2.0 },
    'Serie 2': { type: 'gas', engineSize: 2.0 },
    'Serie 3': { type: 'gas', engineSize: 2.0 },
    'Serie 4': { type: 'gas', engineSize: 3.0 },
    'Serie 5': { type: 'gas', engineSize: 3.0 },
    'Serie 7': { type: 'gas', engineSize: 4.4 },
    'X1': { type: 'gas', engineSize: 2.0 },
    'X3': { type: 'gas', engineSize: 2.0 },
    'X5': { type: 'diesel', engineSize: 3.0 },
    'X6': { type: 'gas', engineSize: 4.4 },
    'i3': { type: 'electric', engineSize: 0 },
    'i4': { type: 'electric', engineSize: 0 },
    'iX': { type: 'electric', engineSize: 0 },
  },
  'Citroën': {
    'C1': { type: 'gas', engineSize: 1.0 },
    'C3': { type: 'gas', engineSize: 1.2 },
    'C4': { type: 'gas', engineSize: 1.5 },
    'C5': { type: 'diesel', engineSize: 2.0 },
    'Berlingo': { type: 'diesel', engineSize: 1.5 },
    'ë-C4': { type: 'electric', engineSize: 0 },
  },
  'Dacia': {
    'Sandero': { type: 'gas', engineSize: 1.0 },
    'Duster': { type: 'gas', engineSize: 1.5 },
    'Logan': { type: 'gas', engineSize: 1.0 },
    'Spring': { type: 'electric', engineSize: 0 },
  },
  'Fiat': {
    '500': { type: 'gas', engineSize: 1.2 },
    'Panda': { type: 'gas', engineSize: 1.2 },
    'Tipo': { type: 'gas', engineSize: 1.4 },
    'Punto': { type: 'gas', engineSize: 1.4 },
    '500e': { type: 'electric', engineSize: 0 },
  },
  'Ford': {
    'Fiesta': { type: 'gas', engineSize: 1.5 },
    'Focus': { type: 'gas', engineSize: 2.0 },
    'Mondeo': { type: 'diesel', engineSize: 2.0 },
    'Kuga': { type: 'gas', engineSize: 2.5 },
    'Puma': { type: 'gas', engineSize: 1.5 },
    'Mustang': { type: 'gas', engineSize: 5.0 },
    'Mustang Mach-E': { type: 'electric', engineSize: 0 },
  },
  'Honda': {
    'Civic': { type: 'gas', engineSize: 1.5 },
    'Jazz': { type: 'hybrid', engineSize: 1.5 },
    'HR-V': { type: 'hybrid', engineSize: 1.5 },
    'CR-V': { type: 'hybrid', engineSize: 2.0 },
    'e': { type: 'electric', engineSize: 0 },
  },
  'Hyundai': {
    'i10': { type: 'gas', engineSize: 1.0 },
    'i20': { type: 'gas', engineSize: 1.2 },
    'i30': { type: 'gas', engineSize: 1.5 },
    'Tucson': { type: 'hybrid', engineSize: 1.6 },
    'Kona': { type: 'gas', engineSize: 1.6 },
    'Ioniq 5': { type: 'electric', engineSize: 0 },
    'Ioniq 6': { type: 'electric', engineSize: 0 },
  },
  'Kia': {
    'Picanto': { type: 'gas', engineSize: 1.0 },
    'Rio': { type: 'gas', engineSize: 1.2 },
    'Ceed': { type: 'gas', engineSize: 1.5 },
    'Sportage': { type: 'hybrid', engineSize: 1.6 },
    'Niro': { type: 'hybrid', engineSize: 1.6 },
    'EV6': { type: 'electric', engineSize: 0 },
  },
  'Mercedes': {
    'Clase A': { type: 'gas', engineSize: 2.0 },
    'Clase B': { type: 'gas', engineSize: 2.0 },
    'Clase C': { type: 'gas', engineSize: 2.0 },
    'Clase E': { type: 'gas', engineSize: 3.0 },
    'Clase S': { type: 'gas', engineSize: 4.0 },
    'GLA': { type: 'gas', engineSize: 2.0 },
    'GLC': { type: 'diesel', engineSize: 2.0 },
    'GLE': { type: 'diesel', engineSize: 3.0 },
    'EQA': { type: 'electric', engineSize: 0 },
    'EQB': { type: 'electric', engineSize: 0 },
    'EQC': { type: 'electric', engineSize: 0 },
    'EQS': { type: 'electric', engineSize: 0 },
  },
  'Nissan': {
    'Micra': { type: 'gas', engineSize: 1.0 },
    'Juke': { type: 'gas', engineSize: 1.6 },
    'Qashqai': { type: 'gas', engineSize: 1.5 },
    'X-Trail': { type: 'gas', engineSize: 1.5 },
    'Leaf': { type: 'electric', engineSize: 0 },
    'Ariya': { type: 'electric', engineSize: 0 },
  },
  'Opel': {
    'Corsa': { type: 'gas', engineSize: 1.2 },
    'Astra': { type: 'gas', engineSize: 1.5 },
    'Mokka': { type: 'gas', engineSize: 1.2 },
    'Grandland': { type: 'gas', engineSize: 1.6 },
    'Corsa-e': { type: 'electric', engineSize: 0 },
    'Mokka-e': { type: 'electric', engineSize: 0 },
  },
  'Peugeot': {
    '108': { type: 'gas', engineSize: 1.0 },
    '208': { type: 'gas', engineSize: 1.2 },
    '308': { type: 'gas', engineSize: 1.5 },
    '408': { type: 'hybrid', engineSize: 1.6 },
    '508': { type: 'hybrid', engineSize: 1.6 },
    '2008': { type: 'gas', engineSize: 1.2 },
    '3008': { type: 'gas', engineSize: 1.6 },
    '5008': { type: 'diesel', engineSize: 2.0 },
    'e-208': { type: 'electric', engineSize: 0 },
    'e-2008': { type: 'electric', engineSize: 0 },
  },
  'Renault': {
    'Clio': { type: 'gas', engineSize: 1.3 },
    'Megane': { type: 'gas', engineSize: 1.5 },
    'Captur': { type: 'gas', engineSize: 1.3 },
    'Kadjar': { type: 'gas', engineSize: 1.5 },
    'Arkana': { type: 'hybrid', engineSize: 1.6 },
    'Scenic': { type: 'gas', engineSize: 1.5 },
    'Zoe': { type: 'electric', engineSize: 0 },
    'Megane E-Tech': { type: 'electric', engineSize: 0 },
  },
  'SEAT': {
    'Ibiza': { type: 'gas', engineSize: 1.5 },
    'Leon': { type: 'gas', engineSize: 2.0 },
    'Arona': { type: 'gas', engineSize: 1.5 },
    'Ateca': { type: 'gas', engineSize: 2.0 },
    'Tarraco': { type: 'diesel', engineSize: 2.0 },
  },
  'Skoda': {
    'Fabia': { type: 'gas', engineSize: 1.5 },
    'Octavia': { type: 'gas', engineSize: 2.0 },
    'Scala': { type: 'gas', engineSize: 1.5 },
    'Karoq': { type: 'gas', engineSize: 2.0 },
    'Kodiaq': { type: 'diesel', engineSize: 2.0 },
    'Enyaq': { type: 'electric', engineSize: 0 },
  },
  'Tesla': {
    'Model 3': { type: 'electric', engineSize: 0 },
    'Model Y': { type: 'electric', engineSize: 0 },
    'Model S': { type: 'electric', engineSize: 0 },
    'Model X': { type: 'electric', engineSize: 0 },
  },
  'Toyota': {
    'Aygo': { type: 'gas', engineSize: 1.0 },
    'Yaris': { type: 'hybrid', engineSize: 1.5 },
    'Corolla': { type: 'hybrid', engineSize: 1.8 },
    'C-HR': { type: 'hybrid', engineSize: 2.0 },
    'RAV4': { type: 'hybrid', engineSize: 2.5 },
    'Land Cruiser': { type: 'diesel', engineSize: 2.8 },
    'bZ4X': { type: 'electric', engineSize: 0 },
  },
  'Volkswagen': {
    'Polo': { type: 'gas', engineSize: 1.5 },
    'Golf': { type: 'gas', engineSize: 2.0 },
    'Passat': { type: 'diesel', engineSize: 2.0 },
    'Tiguan': { type: 'gas', engineSize: 2.0 },
    'T-Roc': { type: 'gas', engineSize: 2.0 },
    'Touareg': { type: 'diesel', engineSize: 3.0 },
    'ID.3': { type: 'electric', engineSize: 0 },
    'ID.4': { type: 'electric', engineSize: 0 },
    'ID.5': { type: 'electric', engineSize: 0 },
  },
  'Volvo': {
    'XC40': { type: 'gas', engineSize: 2.0 },
    'XC60': { type: 'gas', engineSize: 2.0 },
    'XC90': { type: 'diesel', engineSize: 2.0 },
    'S60': { type: 'gas', engineSize: 2.0 },
    'V60': { type: 'gas', engineSize: 2.0 },
    'C40 Recharge': { type: 'electric', engineSize: 0 },
    'XC40 Recharge': { type: 'electric', engineSize: 0 },
  },
};

const BRANDS = Object.keys(CAR_DATABASE).sort();

const EligibilityChecker = ({ isOpen, onClose, onRequestService, lang = "es" }) => {
  const [step, setStep] = useState(1);
  const [isElectric, setIsElectric] = useState(null);
  const [batteryType, setBatteryType] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [customModel, setCustomModel] = useState('');
  const [customEngineSize, setCustomEngineSize] = useState('');
  const [customFuelType, setCustomFuelType] = useState('gas');
  const [result, setResult] = useState(null);

  // Translations
  const translations = {
    es: {
      title: "¿Tu coche es compatible?",
      subtitle: "Verifica si podemos arrancarlo",
      isElectricQuestion: "¿Tu coche es eléctrico?",
      yes: "Sí, es eléctrico",
      no: "No, es gasolina/diésel",
      batteryQuestion: "¿Qué tipo de batería tiene?",
      batteryPlaceholder: "Ej: Litio 75kWh, LFP...",
      brandQuestion: "¿Cuál es la marca?",
      modelQuestion: "¿Cuál es el modelo?",
      selectBrand: "Selecciona marca",
      selectModel: "Selecciona modelo",
      otherModel: "Otro modelo",
      customModelLabel: "Nombre del modelo",
      engineSizeLabel: "Cilindrada (litros)",
      fuelTypeLabel: "Tipo de combustible",
      gasoline: "Gasolina",
      diesel: "Diésel",
      hybrid: "Híbrido",
      check: "Verificar compatibilidad",
      compatible: "¡Tu coche es compatible!",
      compatibleDesc: "Nuestro equipo NOCO GBX45 puede arrancar tu",
      notCompatibleElectric: "Vehículo eléctrico no compatible",
      notCompatibleElectricDesc: "Lo sentimos, no podemos arrancar coches 100% eléctricos. La batería de tracción requiere servicios especializados.",
      notCompatibleSize: "Motor demasiado grande",
      notCompatibleSizeDesc: "Lo sentimos, nuestro equipo soporta hasta 6.5L gasolina o 4L diésel. Tu motor de",
      exceedsLimit: "excede este límite.",
      hybridNote: "Los híbridos utilizan la batería de 12V auxiliar que sí podemos arrancar.",
      requestService: "Solicitar servicio",
      close: "Cerrar",
      back: "Atrás",
      continue: "Continuar",
      specs: "Especificaciones NOCO GBX45:",
      specGas: "Motores gasolina hasta 6.5L",
      specDiesel: "Motores diésel hasta 4L",
    },
    cat: {
      title: "El teu cotxe és compatible?",
      subtitle: "Verifica si el podem arrencar",
      isElectricQuestion: "El teu cotxe és elèctric?",
      yes: "Sí, és elèctric",
      no: "No, és gasolina/dièsel",
      batteryQuestion: "Quin tipus de bateria té?",
      batteryPlaceholder: "Ex: Liti 75kWh, LFP...",
      brandQuestion: "Quina és la marca?",
      modelQuestion: "Quin és el model?",
      selectBrand: "Selecciona marca",
      selectModel: "Selecciona model",
      otherModel: "Altre model",
      customModelLabel: "Nom del model",
      engineSizeLabel: "Cilindrada (litres)",
      fuelTypeLabel: "Tipus de combustible",
      gasoline: "Gasolina",
      diesel: "Dièsel",
      hybrid: "Híbrid",
      check: "Verificar compatibilitat",
      compatible: "El teu cotxe és compatible!",
      compatibleDesc: "El nostre equip NOCO GBX45 pot arrencar el teu",
      notCompatibleElectric: "Vehicle elèctric no compatible",
      notCompatibleElectricDesc: "Ho sentim, no podem arrencar cotxes 100% elèctrics. La bateria de tracció requereix serveis especialitzats.",
      notCompatibleSize: "Motor massa gran",
      notCompatibleSizeDesc: "Ho sentim, el nostre equip suporta fins a 6.5L gasolina o 4L dièsel. El teu motor de",
      exceedsLimit: "excedeix aquest límit.",
      hybridNote: "Els híbrids utilitzen la bateria de 12V auxiliar que sí podem arrencar.",
      requestService: "Sol·licitar servei",
      close: "Tancar",
      back: "Enrere",
      continue: "Continuar",
      specs: "Especificacions NOCO GBX45:",
      specGas: "Motors gasolina fins a 6.5L",
      specDiesel: "Motors dièsel fins a 4L",
    },
    en: {
      title: "Is your car compatible?",
      subtitle: "Check if we can jump-start it",
      isElectricQuestion: "Is your car electric?",
      yes: "Yes, it's electric",
      no: "No, it's gasoline/diesel",
      batteryQuestion: "What type of battery does it have?",
      batteryPlaceholder: "E.g.: Lithium 75kWh, LFP...",
      brandQuestion: "What's the brand?",
      modelQuestion: "What's the model?",
      selectBrand: "Select brand",
      selectModel: "Select model",
      otherModel: "Other model",
      customModelLabel: "Model name",
      engineSizeLabel: "Engine size (liters)",
      fuelTypeLabel: "Fuel type",
      gasoline: "Gasoline",
      diesel: "Diesel",
      hybrid: "Hybrid",
      check: "Check compatibility",
      compatible: "Your car is compatible!",
      compatibleDesc: "Our NOCO GBX45 equipment can jump-start your",
      notCompatibleElectric: "Electric vehicle not compatible",
      notCompatibleElectricDesc: "Sorry, we cannot jump-start 100% electric cars. The traction battery requires specialized services.",
      notCompatibleSize: "Engine too large",
      notCompatibleSizeDesc: "Sorry, our equipment supports up to 6.5L gasoline or 4L diesel. Your",
      exceedsLimit: "engine exceeds this limit.",
      hybridNote: "Hybrids use the auxiliary 12V battery which we can jump-start.",
      requestService: "Request service",
      close: "Close",
      back: "Back",
      continue: "Continue",
      specs: "NOCO GBX45 Specifications:",
      specGas: "Gasoline engines up to 6.5L",
      specDiesel: "Diesel engines up to 4L",
    }
  };

  const t = translations[lang] || translations.es;

  const checkCompatibility = () => {
    let carInfo = null;
    let engineSize = 0;
    let fuelType = 'gas';

    if (selectedBrand && selectedModel && selectedModel !== 'other') {
      carInfo = CAR_DATABASE[selectedBrand]?.[selectedModel];
      if (carInfo) {
        engineSize = carInfo.engineSize;
        fuelType = carInfo.type;
      }
    } else if (customEngineSize) {
      engineSize = parseFloat(customEngineSize);
      fuelType = customFuelType;
    }

    // Check compatibility based on NOCO GBX45 specs
    if (fuelType === 'electric') {
      setResult({
        compatible: false,
        reason: 'electric',
        carName: `${selectedBrand} ${selectedModel !== 'other' ? selectedModel : customModel}`
      });
    } else if (fuelType === 'gas' && engineSize <= 6.5) {
      setResult({
        compatible: true,
        carName: `${selectedBrand} ${selectedModel !== 'other' ? selectedModel : customModel}`,
        engineSize,
        fuelType
      });
    } else if (fuelType === 'diesel' && engineSize <= 4.0) {
      setResult({
        compatible: true,
        carName: `${selectedBrand} ${selectedModel !== 'other' ? selectedModel : customModel}`,
        engineSize,
        fuelType
      });
    } else if (fuelType === 'hybrid') {
      // Hybrids use 12V auxiliary battery
      setResult({
        compatible: true,
        carName: `${selectedBrand} ${selectedModel !== 'other' ? selectedModel : customModel}`,
        engineSize,
        fuelType,
        isHybrid: true
      });
    } else {
      setResult({
        compatible: false,
        reason: 'size',
        carName: `${selectedBrand} ${selectedModel !== 'other' ? selectedModel : customModel}`,
        engineSize,
        fuelType
      });
    }
    
    setStep(4);
  };

  const resetAndClose = () => {
    setStep(1);
    setIsElectric(null);
    setBatteryType('');
    setSelectedBrand('');
    setSelectedModel('');
    setCustomModel('');
    setCustomEngineSize('');
    setCustomFuelType('gas');
    setResult(null);
    onClose();
  };

  const getModels = () => {
    if (!selectedBrand) return [];
    return Object.keys(CAR_DATABASE[selectedBrand] || {}).sort();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Car className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-bold text-lg">{t.title}</h2>
                <p className="text-blue-100 text-sm">{t.subtitle}</p>
              </div>
            </div>
            <button onClick={resetAndClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4 flex space-x-2">
            {[1, 2, 3, 4].map((s) => (
              <div 
                key={s} 
                className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? 'bg-white' : 'bg-white/30'}`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Is it electric? */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 text-lg">{t.isElectricQuestion}</h3>
              
              <div className="space-y-3">
                <button
                  onClick={() => { setIsElectric(true); setStep(2); }}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all flex items-center space-x-4"
                >
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Zap className="h-6 w-6 text-green-600" />
                  </div>
                  <span className="font-medium text-gray-900">{t.yes}</span>
                </button>
                
                <button
                  onClick={() => { setIsElectric(false); setStep(2); }}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all flex items-center space-x-4"
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <Car className="h-6 w-6 text-orange-600" />
                  </div>
                  <span className="font-medium text-gray-900">{t.no}</span>
                </button>
              </div>
              
              {/* Specs info */}
              <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                <p className="text-sm font-medium text-gray-700 mb-2">{t.specs}</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li className="flex items-center space-x-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span>{t.specGas}</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span>{t.specDiesel}</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 2: Electric battery type OR Brand selection */}
          {step === 2 && (
            <div className="space-y-4">
              {isElectric ? (
                <>
                  <h3 className="font-semibold text-gray-900 text-lg">{t.batteryQuestion}</h3>
                  <Input
                    value={batteryType}
                    onChange={(e) => setBatteryType(e.target.value)}
                    placeholder={t.batteryPlaceholder}
                    className="w-full"
                  />
                  <Button
                    onClick={() => {
                      setResult({
                        compatible: false,
                        reason: 'electric',
                        carName: `Vehículo eléctrico (${batteryType || 'batería no especificada'})`
                      });
                      setStep(4);
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {t.check}
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </>
              ) : (
                <>
                  <h3 className="font-semibold text-gray-900 text-lg">{t.brandQuestion}</h3>
                  <select
                    value={selectedBrand}
                    onChange={(e) => { setSelectedBrand(e.target.value); setSelectedModel(''); }}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t.selectBrand}</option>
                    {BRANDS.map((brand) => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                  
                  {selectedBrand && (
                    <Button
                      onClick={() => setStep(3)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {t.continue}
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </>
              )}
              
              <button
                onClick={() => setStep(1)}
                className="w-full flex items-center justify-center space-x-2 text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>{t.back}</span>
              </button>
            </div>
          )}

          {/* Step 3: Model selection */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 text-lg">{t.modelQuestion}</h3>
              <p className="text-sm text-gray-500">{selectedBrand}</p>
              
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t.selectModel}</option>
                {getModels().map((model) => (
                  <option key={model} value={model}>{model}</option>
                ))}
                <option value="other">{t.otherModel}</option>
              </select>
              
              {selectedModel === 'other' && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">{t.customModelLabel}</Label>
                    <Input
                      value={customModel}
                      onChange={(e) => setCustomModel(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">{t.engineSizeLabel}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={customEngineSize}
                      onChange={(e) => setCustomEngineSize(e.target.value)}
                      placeholder="2.0"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">{t.fuelTypeLabel}</Label>
                    <select
                      value={customFuelType}
                      onChange={(e) => setCustomFuelType(e.target.value)}
                      className="w-full mt-1 p-3 border border-gray-300 rounded-lg"
                    >
                      <option value="gas">{t.gasoline}</option>
                      <option value="diesel">{t.diesel}</option>
                      <option value="hybrid">{t.hybrid}</option>
                    </select>
                  </div>
                </div>
              )}
              
              {(selectedModel && selectedModel !== 'other') || (selectedModel === 'other' && customModel && customEngineSize) ? (
                <Button
                  onClick={checkCompatibility}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {t.check}
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : null}
              
              <button
                onClick={() => setStep(2)}
                className="w-full flex items-center justify-center space-x-2 text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>{t.back}</span>
              </button>
            </div>
          )}

          {/* Step 4: Result */}
          {step === 4 && result && (
            <div className="space-y-4">
              {result.compatible ? (
                <div className="text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="h-10 w-10 text-green-600" />
                  </div>
                  <h3 className="font-bold text-xl text-green-600 mb-2">{t.compatible}</h3>
                  <p className="text-gray-600">
                    {t.compatibleDesc} <strong>{result.carName}</strong>
                  </p>
                  {result.isHybrid && (
                    <p className="mt-2 text-sm text-blue-600 bg-blue-50 p-3 rounded-lg">
                      {t.hybridNote}
                    </p>
                  )}
                  
                  <Button
                    onClick={() => { resetAndClose(); onRequestService(); }}
                    className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-bold"
                  >
                    {t.requestService}
                  </Button>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertTriangle className="h-10 w-10 text-red-600" />
                  </div>
                  
                  {result.reason === 'electric' ? (
                    <>
                      <h3 className="font-bold text-xl text-red-600 mb-2">{t.notCompatibleElectric}</h3>
                      <p className="text-gray-600">{t.notCompatibleElectricDesc}</p>
                    </>
                  ) : (
                    <>
                      <h3 className="font-bold text-xl text-red-600 mb-2">{t.notCompatibleSize}</h3>
                      <p className="text-gray-600">
                        {t.notCompatibleSizeDesc} <strong>{result.engineSize}L {result.fuelType === 'diesel' ? 'diesel' : 'gasolina'}</strong> {t.exceedsLimit}
                      </p>
                    </>
                  )}
                  
                  <Button
                    onClick={resetAndClose}
                    variant="outline"
                    className="w-full mt-6 py-6 text-lg font-bold"
                  >
                    {t.close}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EligibilityChecker;
